package one.healthium.ui.compose.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonColors
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.ProvideTextStyle
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import one.healthium.ui.compose.components.AppButtonDefaults.appButtonColors
import one.healthium.ui.compose.components.AppButtonDefaults.invertedPrimaryButtonColors
import one.healthium.ui.compose.components.AppButtonDefaults.primaryButtonColors
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.styles.AppButtonStyleRef
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.white12

val AppButtonHeight = 56.dp

@Composable
fun AppButton(
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
    colors: ButtonColors? = null,
    enabled: Boolean = true,
    shape: Shape = AppButtonDefaults.elevatedShape,
    elevation: Dp? = null,
    contentPadding: PaddingValues? = null,
    minHeight: Dp? = null,
    content: @Composable RowScope.() -> Unit,
) {
    val colors = colors ?: AppButtonDefaults.appButtonColors()
    val interactionSource = remember { MutableInteractionSource() }
    val containerColor = if (enabled) colors.containerColor else colors.disabledContainerColor
    val contentColor = if (enabled) colors.contentColor else colors.disabledContentColor
    Surface(
        onClick = onClick,
        modifier = modifier.semantics { role = Role.Button },
        enabled = enabled,
        shape = shape,
        color = containerColor,
        contentColor = contentColor,
        shadowElevation = elevation ?: AppButtonDefaults.elevatedButtonElevation,
        interactionSource = interactionSource
    ) {
        CompositionLocalProvider(
            LocalTextStyle provides AppTheme.textStyles.button,
            LocalContentColor provides contentColor
        ) {
            Row(
                Modifier
                    .defaultMinSize(
                        minWidth = ButtonDefaults.MinWidth,
                        minHeight = minHeight ?: AppButtonHeight,
                    )
                    .padding(contentPadding ?: AppButtonDefaults.contentPadding),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
                content = content
            )
        }
    }
}

@Composable
fun AppTextButton(
    modifier: Modifier = Modifier,
    style: TextStyle = AppTheme.textStyles.textButton,
    onClick: () -> Unit,
    content: @Composable RowScope.() -> Unit,
) {
    TextButton(
        modifier = modifier,
        onClick = onClick,
        colors = AppButtonDefaults.appTextButtonColors(),
        content = {
            ProvideTextStyle(value = style) {
                content()
            }
        },
    )
}

@Composable
fun AppIconButton(
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
    size: Dp = 60.dp,
    colors: ButtonColors? = null,
    content: @Composable () -> Unit,
) {
    val interactionSource = remember { MutableInteractionSource() }
    val resolvedColors = colors ?: AppButtonDefaults.appButtonColors()

    Box(
        modifier =
            modifier
                .minimumInteractiveComponentSize()
                .size(size)
                .clip(RoundedCornerShape(100))
                .background(color = resolvedColors.containerColor)
                .clickable(
                    onClick = onClick,
                    enabled = true,
                    role = Role.Button,
                    interactionSource = interactionSource,
                    indication = ripple()
                ),
        contentAlignment = Alignment.Center
    ) {
        CompositionLocalProvider(LocalContentColor provides resolvedColors.contentColor) {
            content()
        }
    }
}

@Composable
fun AppProgressButton(
    modifier: Modifier = Modifier,
    text: String,
    onClick: () -> Unit,
    enabled: Boolean = true,
    loading: Boolean = false,
) {
    AppButton(
        modifier = modifier,
        onClick = onClick,
        enabled = enabled && !loading,
    ) {
        if (loading) {
            CircularProgressIndicator(
                Modifier.size(24.dp),
                strokeWidth = 2.dp,
            )
        } else {
            Text(text)
        }
    }
}

@Composable
fun AppOutlinedButton(
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
    enabled: Boolean = true,
    shape: Shape = AppButtonDefaults.elevatedShape,
    contentPadding: PaddingValues = AppButtonDefaults.contentPadding,
    content: @Composable RowScope.() -> Unit,
) {
    OutlinedButton(
        modifier = modifier,
        onClick = onClick,
        enabled = enabled,
        shape = shape,
        colors = AppButtonDefaults.appOutlinedButtonColors(),
        contentPadding = contentPadding,
        border = BorderStroke(
            width = 1.dp,
            color = if (enabled) white else white12
        ),
        content = {
            ProvideTextStyle(value = AppTheme.textStyles.button) {
                content()
            }
        },
    )
}

object AppButtonDefaults {
    val elevatedButtonElevation
        @Composable get() = 1.dp

    val elevatedButtonElevationZero
        @Composable get() = 0.dp

    val elevatedShape
        @Composable get() = AppTheme.shapes.button

    val contentPadding = PaddingValues(start = 24.dp, top = 8.dp, end = 24.dp, bottom = 8.dp)

    @Composable
    fun appButtonColors(
        containerColor: Color? = null,
        contentColor: Color? = null,
    ) = ButtonDefaults.elevatedButtonColors(
        containerColor = containerColor ?: AppTheme.defaultButtonStyle?.containerColor ?: AppTheme.colors.primary,
        contentColor = contentColor ?: AppTheme.defaultButtonStyle?.contentColor ?: AppTheme.colors.onPrimary,
    )

    @Composable
    fun appOutlinedButtonColors(
        containerColor: Color? = null,
        contentColor: Color? = null,
    ) = ButtonDefaults.outlinedButtonColors(
        containerColor = containerColor ?: Color.Transparent,
        contentColor = contentColor ?: AppTheme.colors.onPrimary,
    )

    @Composable
    fun resolveColors(ref: AppButtonStyleRef) = when (ref) {
        AppButtonStyleRef.Default -> appButtonColors()
        AppButtonStyleRef.Primary -> primaryButtonColors
        AppButtonStyleRef.PrimaryInverted -> invertedPrimaryButtonColors
    }

    @Composable
    fun appTextButtonColors(
        contentColor: Color? = null,
    ) = ButtonDefaults.textButtonColors(
        contentColor = contentColor ?: AppTheme.defaultButtonStyle?.containerColor ?: AppTheme.colors.primary,
    )

    val primaryButtonColors
        @Composable get() = appButtonColors(
            containerColor = AppTheme.colors.primary,
            contentColor = AppTheme.colors.onPrimary,
        )

    val secondaryButtonColors
        @Composable get() = appButtonColors(
            containerColor = AppTheme.colors.secondary,
            contentColor = AppTheme.colors.onSecondary,
        )

    val invertedPrimaryButtonColors
        @Composable get() = appButtonColors(
            containerColor = AppTheme.colors.onPrimary,
            contentColor = AppTheme.colors.primary,
        )

    val invertedSecondaryButtonColors
        @Composable get() = appButtonColors(
            containerColor = AppTheme.colors.onSecondary,
            contentColor = AppTheme.colors.secondary,
        )
}

@Composable
fun AppButtonStyleRef.resolve(): ButtonColors {
    return when (this) {
        AppButtonStyleRef.Default -> appButtonColors()
        AppButtonStyleRef.Primary -> primaryButtonColors
        AppButtonStyleRef.PrimaryInverted -> invertedPrimaryButtonColors
    }
}
